/**
 * Playbooks, skills and methods are markdown with `## ` sections. A section's id is its heading as a
 * slug, so a goal can keep its own words for one section (config.library[].edits), and two versions can
 * be compared section by section for "update available".
 */
export interface Section {
  id: string;
  title: string;
  text: string;
}

export const sectionId = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** The text before the first section, then each `## ` section with its body (subsections included). */
export function splitSections(body: string): { intro: string; sections: Section[] } {
  const lines = body.split(/\r?\n/);
  const intro: string[] = [];
  const sections: Section[] = [];
  let cur: Section | null = null;
  for (const line of lines) {
    const h = line.match(/^## (.+)$/);
    if (h) {
      cur = { id: sectionId(h[1]), title: h[1].trim(), text: "" };
      sections.push(cur);
    } else if (cur) {
      cur.text += (cur.text ? "\n" : "") + line;
    } else {
      intro.push(line);
    }
  }
  for (const s of sections) s.text = s.text.trim();
  return { intro: intro.join("\n").trim(), sections };
}

/** The body with the owner's words in place of the sections they edited. Unknown ids are ignored. */
export function applyEdits(body: string, edits: Record<string, string>): string {
  if (!Object.keys(edits).length) return body;
  const { intro, sections } = splitSections(body);
  const parts = [intro];
  for (const s of sections) parts.push(`## ${s.title}\n${(edits[s.id] ?? s.text).trim()}`);
  return parts.filter(Boolean).join("\n\n") + "\n";
}

export interface SectionChange {
  id: string;
  title: string;
  change: "added" | "removed" | "changed";
}

/** What changed from one version to the next, section by section. */
export function diffSections(older: string, newer: string): SectionChange[] {
  const a = new Map(splitSections(older).sections.map((s) => [s.id, s]));
  const b = splitSections(newer).sections;
  const out: SectionChange[] = [];
  for (const s of b) {
    const prev = a.get(s.id);
    if (!prev) out.push({ id: s.id, title: s.title, change: "added" });
    else if (prev.text !== s.text) out.push({ id: s.id, title: s.title, change: "changed" });
    a.delete(s.id);
  }
  for (const s of a.values()) out.push({ id: s.id, title: s.title, change: "removed" });
  return out;
}

/** Edits whose section is gone in a newer version, so the owner can carry them over by hand. */
export const orphanedEdits = (body: string, edits: Record<string, string>) => {
  const ids = new Set(splitSections(body).sections.map((s) => s.id));
  return Object.keys(edits).filter((id) => !ids.has(id));
};
